import React, { useCallback, useEffect, useState, useMemo } from "react";
import styled, { withTheme } from "styled-components";
import {
  studiesIds,
  studyStatusesGSDIII,
  studyStatusesFAOD,
} from "../../../modules/constants";
import AlertUGNX from "../../ToastsAndAlerts/AlertUGNX";
import { AlertType } from "../../ToastsAndAlerts/constants";
import CustomDropdown from "../../ui-library/CustomDropdown/CustomDropdown";
import LabelledInput from "../../ui-library/LabelledInput/LabelledInput";
import SetDailyGoals from "../SetDailyGoals/SetDailyGoals";
import PrimaryButton from "../../ui-library/Buttons/PrimaryButton/PrimaryButton";
import { isNumeric } from "../../../modules/utils";
import Checkbox from "../../ui-library/Checkbox/Checkbox";
import { getLanguages } from "../../../i18n/helpers";

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const StyledContainerVertical = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledContainerHorizontal = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const StyledFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  @media (max-width: 576px) {
    padding-top: 2rem;
    margin-top: auto;
    .btn {
      width: 100%;
    }
  }
`;

const StyledLabel = styled.label`
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
`;

const StyledError = styled.div`
  font-weight: 600;
  font-size: 12px;
  color: ${(props) => props.theme?.cardinal};
  text-transform: uppercase;
`;

const SetDailyGoalsWrapper = styled.div`
  background-color: ${(props) => props.theme?.whiteSmoke};
  padding: 15px 30px 15px 30px;
  margin-bottom: 15px;
`;

const minDate = 1900;
const presentYear = new Date().getFullYear();

const numberRegex = "[0-9]{3}";
const ugnxIdRegex = new RegExp(`^${numberRegex}(-${numberRegex})$`);
const ugnxIdInputRegex = new RegExp("^([0-9]{1,3}-)(?:[0-9]{1,3})?$");
const yearRegex = new RegExp("^[0-9]{1,4}$");

const AddNewParticipant = (props) => {
  const { onSubmit, studyId, siteId, hasError } = props;

  const [ugnxId, setUgnxId] = useState("");
  const [yearOfBirth, setYearOfBirth] = useState("");
  const [isConsentSigned, setIsConsentSigned] = useState(false);
  const [isIdVerified, setIsIdVerified] = useState(false);
  const [isDiagnosisConfirmed, setIsDiagnosisConfirmed] = useState(false);
  const [studyStatus, setStudyStatus] = useState(null);
  const [language, setLanguage] = useState("en-US");

  const [dailyGoals, setDailyGoals] = useState(null);
  const [isDailyGoalsValid, setIsDailyGoalsValid] = useState(false);
  const [validateDailyGoals, setValidateDailyGoals] = useState(false);

  const [submitting, setSubmitting] = useState(false);

  const availableLanguages = useMemo(
    () => getLanguages().filter((language) => language.isAvailable),
    []
  );

  const studyStatusDataSet = useMemo(
    () =>
      studyId.toLocaleLowerCase() === studiesIds.lcfaod401.toLocaleLowerCase()
        ? [studyStatusesFAOD.ACTIVE_DMP]
        : [studyStatusesGSDIII.SAD_PERIOD, studyStatusesGSDIII.RD_ISV],
    [studyId]
  );

  const isRdIsv = useMemo(() => {
    return studyStatus?.id === studyStatusesGSDIII.RD_ISV.id;
  }, [studyStatus]);

  const submitText = useMemo(
    () => (isRdIsv ? "Add participant and goal" : "Add participant"),
    [isRdIsv]
  );

  const [errors, setErrors] = useState({});

  const isSadPeriod = useMemo(
    () => studyStatus?.id === studyStatusesGSDIII.SAD_PERIOD.id,
    [studyStatus]
  );

  const handleUgnxIdOnChange = (value) => {
    const ugnxIDFirstNumbersRegex = new RegExp(`^${numberRegex}$`);

    if (isNumeric(value)) {
      //avoid adding "-" if the user is deleting chars
      if (
        ugnxId.length < value.length &&
        new RegExp(ugnxIDFirstNumbersRegex).test(value)
      ) {
        setUgnxId(`${value}-`);
      } else if (value.length >= 4 && !value.includes("-")) {
        setUgnxId(
          `${value.substring(0, 3)}-${value.substring(3, value.length)}`
        );
      } else {
        setUgnxId(value);
      }
    } else if (value === "" || ugnxIdInputRegex.test(value)) {
      setUgnxId(value);
    }
  };

  const handleYearOfBirthDateChange = (value) => {
    if (value === "" || (value && yearRegex.test(value))) {
      setYearOfBirth(value);
    }
  };

  const handleConsentSignedOnchange = (value) => {
    validateConsentSigned(!value);
    setIsConsentSigned(!value);
  };

  const handleVerifiedIDOnChange = (value) => {
    validateVerifiedID(!value);
    setIsIdVerified(!value);
  };

  const handleDiagnosisConfirmedChange = (value) => {
    validateDiagnosisConfirmed(!value);
    setIsDiagnosisConfirmed(!value);
  };

  const handleStudyStatusOnChange = (value) => {
    validadeStudyStatus(value);
    setStudyStatus(value);
  };

  useEffect(() => {
    setDailyGoals(null);
  }, [studyStatus]);

  useEffect(() => {
    if (hasError) {
      setSubmitting(false);
    }
  }, [hasError]);

  const validadeYearBirth = () => {
    setErrors({
      ...errors,
      yearOfBirth: isYearBirthValid()
        ? undefined
        : `The date must be between ${minDate} and ${presentYear}`,
    });
  };

  const validadeUgnxId = () => {
    if (ugnxId === "") {
      setErrors({
        ...errors,
        ugnxId: isUgnxIdValid() ? undefined : "This field is required.",
      });
    } else {
      setErrors({
        ...errors,
        ugnxId: isUgnxIdValid() ? undefined : "Invalid format, must be XXX-XXX",
      });
    }
  };

  const validateConsentSigned = (value) => {
    setErrors({
      ...errors,
      isConsentSigned: isConsentSignedValid(value)
        ? undefined
        : "This field is required.",
    });
  };

  const validateVerifiedID = (value) => {
    setErrors({
      ...errors,
      isIdVerified: isVerifiedIDValid(value)
        ? undefined
        : "This field is required.",
    });
  };

  const validateDiagnosisConfirmed = (value) => {
    setErrors({
      ...errors,
      isDiagnosisConfirmed: isDiagnosisConfirmedValid(value)
        ? undefined
        : "This field is required.",
    });
  };

  const validadeStudyStatus = (value) => {
    setErrors({
      ...errors,
      studyStatus: isStudyStatusValid(value)
        ? undefined
        : "This field is required.",
    });
  };

  const onValidateDailyTargets = useCallback((isSetDailyGoalsValid, goals) => {
    setIsDailyGoalsValid(isSetDailyGoalsValid);

    if (isSetDailyGoalsValid) {
      setDailyGoals(goals);
    } else {
      setValidateDailyGoals(false);
      setDailyGoals(null);
    }
  }, []);

  const isYearBirthValid = useCallback(() => {
    if (yearOfBirth < minDate || yearOfBirth > presentYear) {
      return false;
    }
    return true;
  }, [yearOfBirth]);

  const isUgnxIdValid = useCallback(() => {
    if (!ugnxIdRegex.test(ugnxId)) {
      return false;
    }
    return true;
  }, [ugnxId]);

  const isConsentSignedValid = useCallback((value) => {
    return value;
  }, []);

  const isVerifiedIDValid = useCallback((value) => {
    return value;
  }, []);

  const isDiagnosisConfirmedValid = useCallback((value) => {
    return value;
  }, []);

  const isStudyStatusValid = useCallback((value) => {
    return !!value;
  }, []);

  const validadeBeforeSubmit = (event) => {
    event.preventDefault();
    checkErrors();

    if (isParentFormValid()) {
      if (isRdIsv && !dailyGoals && !isDailyGoalsValid) {
        setValidateDailyGoals(true);
      } else {
        submitForm();
      }
    }
  };

  const checkErrors = useCallback(() => {
    setErrors((prevState) => ({
      ...prevState,
      yearOfBirth: isYearBirthValid()
        ? undefined
        : `The date must be between ${minDate} and ${new Date().getFullYear()}`,
      ugnxId:
        ugnxId === ""
          ? "This field is required."
          : isUgnxIdValid()
          ? undefined
          : "Invalid format, must be XXX-XXX",
      isConsentSigned: isConsentSignedValid(isConsentSigned)
        ? undefined
        : "This field is required.",
      isIdVerified: isVerifiedIDValid(isIdVerified)
        ? undefined
        : "This field is required.",
      isDiagnosisConfirmed: isDiagnosisConfirmedValid(isDiagnosisConfirmed)
        ? undefined
        : "This field is required.",
      studyStatus: isStudyStatusValid(studyStatus)
        ? undefined
        : "This field is required.",
    }));
  }, [
    isYearBirthValid,
    ugnxId,
    isUgnxIdValid,
    isConsentSignedValid,
    isConsentSigned,
    isVerifiedIDValid,
    isIdVerified,
    isDiagnosisConfirmedValid,
    isDiagnosisConfirmed,
    isStudyStatusValid,
    studyStatus,
  ]);

  const isParentFormValid = () =>
    isYearBirthValid() &&
    isUgnxIdValid() &&
    isConsentSignedValid(isConsentSigned) &&
    isVerifiedIDValid(isIdVerified) &&
    isDiagnosisConfirmedValid(isDiagnosisConfirmed) &&
    isStudyStatusValid(studyStatus);

  const submitForm = useCallback(() => {
    const formData = {
      studyId,
      siteId,
      ugnxId,
      yearOfBirth: parseInt(yearOfBirth),
      signedConsent: isConsentSigned,
      idVerified: isIdVerified,
      diagnosisConfirmed: isDiagnosisConfirmed,
      language,
      studyStatus: studyStatus?.id,
      ...(isRdIsv && {
        dailyTarget: dailyGoals,
      }),
    };

    setSubmitting(true);
    onSubmit(formData);
  }, [
    studyId,
    siteId,
    ugnxId,
    yearOfBirth,
    isConsentSigned,
    isIdVerified,
    isDiagnosisConfirmed,
    studyStatus,
    language,
    isRdIsv,
    dailyGoals,
    onSubmit,
  ]);

  useEffect(() => {
    if (dailyGoals) {
      setValidateDailyGoals(false);
    }
  }, [dailyGoals]);

  useEffect(() => {
    if (dailyGoals && isDailyGoalsValid && !submitting) {
      submitForm();
    }
  }, [dailyGoals, isDailyGoalsValid, submitForm, submitting]);

  return (
    <FormContainer data-testid="addnewparticipantId">
      <LabelledInput
        tabIndex={1}
        data-testid="ugnxID-input"
        className="mt-4"
        title="Participant ID:"
        value={ugnxId}
        type="text"
        placeholder="XXX-XXX"
        hasError={!!errors?.ugnxId}
        errorMessage={errors?.ugnxId}
        onChange={(e) => handleUgnxIdOnChange(e.target.value)}
        onBlur={() => validadeUgnxId()}
      />

      <LabelledInput
        tabIndex={2}
        data-testid="yearOfBirth-input"
        className="mt-4"
        title="Year of birth:"
        min={minDate}
        max={presentYear}
        value={yearOfBirth}
        type="text"
        placeholder="YYYY"
        pattern="[0-9]*"
        hasError={!!errors?.yearOfBirth}
        errorMessage={errors?.yearOfBirth}
        onChange={(e) => {
          e.target.validity.valid &&
            handleYearOfBirthDateChange(e.target.value);
        }}
        onBlur={() => validadeYearBirth()}
      />

      <span className="mt-4">
        <Checkbox
          tabIndex={3}
          data-testid="consentSigned-checkbox"
          checked={isConsentSigned}
          onClick={(e) => handleConsentSignedOnchange(isConsentSigned)}
          icon={true}
          square={true}
        />
        <StyledLabel
          className="ml-2"
          onClick={(e) => handleConsentSignedOnchange(isConsentSigned)}
        >
          Informed consent signed
        </StyledLabel>
      </span>
      <StyledError>
        {errors?.isConsentSigned && <span>{errors?.isConsentSigned}</span>}
      </StyledError>

      <span className="mt-2">
        <Checkbox
          tabIndex={4}
          data-testid="verifiedID-checkbox"
          checked={isIdVerified}
          onClick={(e) => handleVerifiedIDOnChange(isIdVerified)}
          icon={true}
          square={true}
        />
        <StyledLabel
          className="ml-2"
          onClick={(e) => handleVerifiedIDOnChange(isIdVerified)}
        >
          ID verified
        </StyledLabel>
      </span>
      <StyledError>
        {errors?.isIdVerified && <span>{errors?.isIdVerified}</span>}
      </StyledError>

      <span className="mt-2">
        <Checkbox
          tabIndex={5}
          data-testid="diagnosis-confirmed-checkbox"
          checked={isDiagnosisConfirmed}
          onClick={(e) => handleDiagnosisConfirmedChange(isDiagnosisConfirmed)}
          icon={true}
          square={true}
        />
        <StyledLabel
          className="ml-2"
          onClick={(e) => handleDiagnosisConfirmedChange(isDiagnosisConfirmed)}
        >
          Diagnosis confirmed
        </StyledLabel>
      </span>
      <StyledError>
        {errors?.isDiagnosisConfirmed && (
          <span>{errors?.isDiagnosisConfirmed}</span>
        )}
      </StyledError>

      <StyledContainerHorizontal className="mt-4">
        <StyledLabel className="mb-0">Study Status:</StyledLabel>
        <CustomDropdown
          tabIndexProp={6}
          placeholder="Select"
          values={studyStatusDataSet}
          onChange={(e) => handleStudyStatusOnChange(e)}
          width={200}
        />
      </StyledContainerHorizontal>
      <StyledError>
        {errors?.studyStatus && <span>{errors?.studyStatus}</span>}
      </StyledError>

      <StyledContainerVertical className="mt-4">
        {isSadPeriod && (
          <StyledLabel>*SAD Period - No nutritional targets</StyledLabel>
        )}
        {isRdIsv && (
          <StyledLabel>*RD: ISV - Include nutritional targets</StyledLabel>
        )}
      </StyledContainerVertical>
      {isRdIsv && (
        <SetDailyGoalsWrapper className="mt-4">
          <SetDailyGoals
            validate={validateDailyGoals}
            onValidate={onValidateDailyTargets}
          />
        </SetDailyGoalsWrapper>
      )}

      <StyledContainerHorizontal className="mt-2">
        <StyledLabel>Select language:</StyledLabel>
        <CustomDropdown
          tabIndexProp={7}
          placeholder={
            getLanguages().find((language) => language.code === "en-US")
              ?.value ?? "Select"
          }
          values={availableLanguages}
          width={145}
          onChange={(lang) => setLanguage(lang.code)}
        />
      </StyledContainerHorizontal>
      <div className="mt-4">
        <AlertUGNX type={AlertType.WARNING}>
          Please confirm that you've entered all data precisely.
        </AlertUGNX>
      </div>

      <StyledFooter>
        <PrimaryButton
          tabIndex={8}
          data-testid="submitparticipant"
          onClick={(e) => validadeBeforeSubmit(e)}
        >
          {submitText}
        </PrimaryButton>
      </StyledFooter>
    </FormContainer>
  );
};

export default withTheme(AddNewParticipant);
